import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Grid,
  Divider,
  IconButton,
} from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { createRoutinePreviousVaccinationRecord } from "./routineVaccinationApi";

const formatAdministeredDate = (dateValue) => {
  if (!dateValue) return "N/A";
  const parsed = dayjs(dateValue);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY") : "N/A";
};

const extractSelectedDoses = (vaccines = []) =>
  vaccines.flatMap((vaccine) =>
    (vaccine.availableDoses || [])
      .filter((dose) => dose.selected)
      .map((dose) => ({
        vaccineName: vaccine.name,
        doseName: dose.doseName,
        administeredDate: dose.administeredDate || null,
      }))
  );

const VaccinationSummary = ({
  vaccinationData,
  selectedVaccinations,
  beneficiaryData,
  onComplete,
  onCancel,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const summaryData = useMemo(() => {
    if (vaccinationData?.selectedVaccines) {
      return extractSelectedDoses(vaccinationData.selectedVaccines);
    }

    return extractSelectedDoses(
      (selectedVaccinations || []).filter((vaccine) => vaccine.selected)
    );
  }, [vaccinationData, selectedVaccinations]);

  const handleFinalSubmit = async () => {
    try {
      setIsSubmitting(true);

      if (summaryData.length === 0) {
        toast.error("No vaccinations to save");
        return;
      }

      const resolvedBeneficiaryData =
        vaccinationData?.beneficiaryData || beneficiaryData;

      if (!resolvedBeneficiaryData?.beneficiary_id) {
        toast.error("Beneficiary information is missing");
        return;
      }

      const formData = vaccinationData
        ? {
            previous_vaccines:
              vaccinationData.previous_vaccines ||
              vaccinationData.selectedVaccines,
          }
        : { previous_vaccines: selectedVaccinations };

      const result = await createRoutinePreviousVaccinationRecord(
        formData,
        resolvedBeneficiaryData
      );

      if (result.success) {
        toast.success(result.message);
        if (onComplete) {
          onComplete(result);
        }
      }
    } catch (error) {
      console.error("Error saving vaccination record:", error);
      toast.error(error?.message || "Failed to save vaccination record");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: 3, minWidth: 600, maxWidth: 800 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 3,
        }}
      >
        {onCancel && (
          <IconButton onClick={onCancel} size="small" aria-label="Back to form">
            <ArrowBackIcon />
          </IconButton>
        )}
        <Typography
          variant="h5"
          sx={{
            fontFamily: "Lato, -apple-system, BlinkMacSystemFont, sans-serif",
            fontWeight: 600,
            color: "#111827",
          }}
        >
          Vaccination Summary
        </Typography>
      </Box>

      <Box sx={{ p: 3, borderRadius: 2 }}>
        {summaryData.length === 0 ? (
          <Typography color="text.secondary">
            No vaccinations selected for recording.
          </Typography>
        ) : (
          <Box>
            {summaryData.map((item, index) => (
              <Box key={index}>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#6b7280",
                          fontSize: "12px",
                          fontWeight: 500,
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Vaccine Name
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          fontFamily:
                            "Lato, -apple-system, BlinkMacSystemFont, sans-serif",
                          fontWeight: 500,
                          fontSize: "16px",
                          color: "#111827",
                          mt: 0.5,
                        }}
                      >
                        {item.vaccineName}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={6} sm={4}>
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#6b7280",
                          fontSize: "12px",
                          fontWeight: 500,
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Dose
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          fontFamily:
                            "Lato, -apple-system, BlinkMacSystemFont, sans-serif",
                          fontWeight: 500,
                          fontSize: "16px",
                          color: "#111827",
                          mt: 0.5,
                        }}
                      >
                        {item.doseName}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={6} sm={4}>
                    <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#6b7280",
                          fontSize: "12px",
                          fontWeight: 500,
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Vaccine Received
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          fontFamily:
                            "Lato, -apple-system, BlinkMacSystemFont, sans-serif",
                          fontWeight: 500,
                          fontSize: "16px",
                          color: item.administeredDate ? "#10b981" : "#6b7280",
                          mt: 0.5,
                        }}
                      >
                        {formatAdministeredDate(item.administeredDate)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {index < summaryData.length - 1 && <Divider sx={{ my: 3 }} />}
              </Box>
            ))}
          </Box>
        )}

        <Box
          sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 4 }}
        >
          {onCancel && (
            <Button
              variant="outlined"
              onClick={onCancel}
              disabled={isSubmitting}
              sx={{
                fontFamily:
                  "Lato, -apple-system, BlinkMacSystemFont, sans-serif",
                textTransform: "none",
                borderRadius: 2,
              }}
            >
              Back to Form
            </Button>
          )}

          <Button
            variant="contained"
            onClick={handleFinalSubmit}
            disabled={isSubmitting || summaryData.length === 0}
            startIcon={
              isSubmitting && <CircularProgress size={20} color="inherit" />
            }
            sx={{
              fontFamily:
                "Lato, -apple-system, BlinkMacSystemFont, sans-serif",
              textTransform: "none",
              borderRadius: 2,
              minWidth: 120,
              backgroundColor: "#1976d2",
              "&:hover": {
                backgroundColor: "#1565c0",
              },
            }}
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default VaccinationSummary;
